import { useEffect, useState } from "react"

interface TimezoneInfo {
  timezone: string // e.g. "America/New_York"
  offset: string // e.g. "UTC-05:00"
  abbreviation: string // e.g. "EST"
  displayTimezoneName: string // e.g. "Eastern Standard Time"
}

export const useTimezone = (): TimezoneInfo => {
  const [timezoneInfo, setTimezoneInfo] = useState<TimezoneInfo>({
    timezone: "",
    offset: "",
    abbreviation: "",
    displayTimezoneName: "",
  })

  useEffect(() => {
    const getTimezoneInfo = (): TimezoneInfo => {
      // Get timezone name
      const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone

      // Get current date for calculations
      const date = new Date()

      // Get timezone offset in minutes and convert to hours:minutes format
      const offsetMinutes = date.getTimezoneOffset()
      const offsetHours = Math.abs(Math.floor(offsetMinutes / 60))
      const offsetMins = Math.abs(offsetMinutes % 60)
      const offsetSign = offsetMinutes <= 0 ? "+" : "-"
      const offset = `UTC${offsetSign}${offsetHours.toString().padStart(2, "0")}:${offsetMins.toString().padStart(2, "0")}`

      // Get abbreviation from the date string
      // This is a bit hacky but works in most cases
      const tzAbbr = date
        .toLocaleTimeString("en-us", { timeZoneName: "short" })
        .split(" ")[2]

      // Get full display name
      const displayName = date
        .toLocaleTimeString("en-us", { timeZoneName: "long" })
        .split(" ")
        .slice(2)
        .join(" ")

      return {
        timezone:
          timezoneName === "Asia/Calcutta" ? "Asia/Kolkata" : timezoneName,
        offset,
        abbreviation: tzAbbr || "",
        displayTimezoneName: displayName || timezoneName,
      }
    }

    setTimezoneInfo(getTimezoneInfo())

    // Update timezone info when DST might change
    const interval = setInterval(
      () => {
        setTimezoneInfo(getTimezoneInfo())
      },
      60 * 60 * 1000,
    ) // Check hourly

    return () => clearInterval(interval)
  }, [])

  return timezoneInfo
}
